import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../components/base/BasicDialog/BasicDialog";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import {
    useOfferCancellationReasons,
    useOfferStatuses,
    useUpdateOfferStatusMutation,
} from "../../services/offersService";

export default function OfferFiascoDialog({ open, onClose, offerId, onSuccess }) {
    const { t } = useTranslation();
    const [selectedReasonId, setSelectedReasonId] = useState("");
    const [resonNote, setReasonNote] = useState("");
    const [error, setError] = useState("");

    const { data: cancellationReasons, isLoading: isLoadingReasons } = useOfferCancellationReasons({
        select: (data) => data.results,
        enabled: open,
    });

    const { data: statuses, isLoading: isLoadingStatuses } = useOfferStatuses({
        select: (data) => data.results,
        enabled: open,
    });

    const { mutate: updateOfferStatus, isPending: isUpdating } = useUpdateOfferStatusMutation();

    useEffect(() => {
        if (open) {
            setSelectedReasonId("");
            setError("");
            setReasonNote("")
        }
    }, [open]);

    const handleChangeReason = (event) => {
        setSelectedReasonId(event.target.value);
        setError("");
    };

    const handleConfirm = () => {
        if (!selectedReasonId) {
            setError(t("offers.fiascoDialog.reasonRequired"));
            return;
        }

        const fiascoStatus = statuses?.find((status) => status.status_key === "Failure");

        if (!fiascoStatus) {
            setError(t("offers.fiascoDialog.fiascoStatusNotFound"));
            return;
        }

        updateOfferStatus(
            { offerId, statusId: fiascoStatus.id, reasonId: selectedReasonId, statusNote: resonNote },
            {
                onSuccess: () => {
                    toast.custom(
                        () => <SnackbarSuccess message={t("offers.fiascoDialog.successMessage")} />,
                        {
                            position: "bottom-right",
                        },
                    );
                    onClose();
                    if (onSuccess) {
                        onSuccess();
                    }
                },
                onError: (error) => {
                    toast.custom(
                        () => <SnackbarError message={t("offers.fiascoDialog.errorMessage")} />,
                        {
                            position: "bottom-right",
                        },
                    );
                },
            },
        );
    };

    return (
        <BasicDialog
            open={open}
            onClose={onClose}
            title={t("offers.fiascoDialog.title")}
            maxWidth="sm"
            showBottomCloseButton={false}
            showDialogActions={false}
        >
            <Box sx={{ p: 2 }}>
                <FormControl
                    fullWidth
                    error={!!error}
                    disabled={isLoadingReasons || isUpdating || isLoadingStatuses}
                >
                    <InputLabel id="offer-fiasco-reason-label">
                        {t("offers.fiascoDialog.reasonLabel")}
                    </InputLabel>
                    <Select
                        labelId="offer-fiasco-reason-label"
                        value={selectedReasonId}
                        label={t("offers.fiascoDialog.reasonLabel")}
                        onChange={handleChangeReason}
                    >
                        {cancellationReasons?.map((reason) => (
                            <MenuItem key={reason.id} value={reason.id}>
                                {t(`offers.cancellationReasons.${reason.reason_name_eng}`)}
                            </MenuItem>
                        ))}
                    </Select>
                    {error && <FormHelperText>{error}</FormHelperText>}
                    <TextField
                        sx={{marginTop: 2}}
                        labelId="offer-fiasco-reason-note_label"
                        value={resonNote}
                        label={t("offers.fiascoDialog.reasonNoteLabel")}
                        onChange={(e)=>setReasonNote(e.target.value)}
                        multiline
                        rows={2}
                    />
                    
                </FormControl>

                <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <Button variant="outlined" onClick={onClose} disabled={isUpdating} fullWidth>
                        {t("cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirm}
                        disabled={isLoadingReasons || isUpdating || isLoadingStatuses}
                        fullWidth
                    >
                        {t("confirm")}
                    </Button>
                </Box>
            </Box>
        </BasicDialog>
    );
}
